interface CategoryWeights {
  outcomes: number;
  expertOpinion: number;
  facultyResources: number;
  financialResources: number;
  studentExcellence: number;
  facultyResearch: number;
}

interface DetailedMetricWeights {
  outcomes: {
    graduationRate: number;
    retentionRate: number;
    pellGrantGradRate: number;
    nonPellGrantGradRate: number;
    overperformance: number;
    medianDebt: number;
    graduatesEarning: number;
  };
  expertOpinion: {
    peerAssessment: number;
  };
  facultyResources: {
    facultySalaries: number;
    fullTimeFaculty2: number;
    fullTimeFaculty3: number;
    studentFacultyRatio: number;
  };
  financialResources: {
    financialResourcesRank: number;
  };
  studentExcellence: {
    satAct75thPercentile: number;
  };
  facultyResearch: {
    bibliometricRank: number;
  };
}

interface CategoryMetrics {
  outcomes: number;
  expertOpinion: number;
  facultyResources: number;
  financialResources: number;
  studentExcellence: number;
  facultyResearch: number;
}

const CATEGORY_WEIGHTS: CategoryWeights = {
  outcomes: 0.52,           // 52%
  expertOpinion: 0.20,      // 20%
  facultyResources: 0.11,   // 11%
  financialResources: 0.08, // 8%
  studentExcellence: 0.05,  // 5%
  facultyResearch: 0.04,    // 4%
};

const DETAILED_METRIC_WEIGHTS: DetailedMetricWeights = {
  outcomes: {
    graduationRate: 0.16,           // 16%
    retentionRate: 0.05,            // 5%
    pellGrantGradRate: 0.055,       // 5.5%
    nonPellGrantGradRate: 0.055,    // 5.5%
    overperformance: 0.10,          // 10%
    medianDebt: 0.05,               // 5%
    graduatesEarning: 0.05,         // 5%
  },
  expertOpinion: {
    peerAssessment: 0.20,           // 20%
  },
  facultyResources: {
    facultySalaries: 0.06,          // 6%
    fullTimeFaculty2: 0.02,         // 2%
    fullTimeFaculty3: 0.03,         // 3%
    studentFacultyRatio: 0.03,      // 3%
  },
  financialResources: {
    financialResourcesRank: 0.08,    // 8%
  },
  studentExcellence: {
    satAct75thPercentile: 0.05,     // 5%
  },
  facultyResearch: {
    bibliometricRank: 0.04,         // 4%
  },
};

export function calculateRankFromMetrics(metrics: CategoryMetrics): number {
  // Calculate weighted score
  let totalScore = 0;
  
  // Apply weights to each category
  for (const [category, value] of Object.entries(metrics)) {
    const weight = CATEGORY_WEIGHTS[category as keyof CategoryWeights];
    totalScore += value * weight;
  }

  // Convert score to rank (baseline of 200, can improve up to rank 1)
  const baseRank = 200 - (totalScore * 100);
  
  // Add small random variance (+/- 3 positions) for realism
  const variance = (Math.random() * 6) - 3;
  
  // Ensure rank stays within bounds (1-200)
  return Math.max(1, Math.min(200, Math.round(baseRank + variance)));
}

// Helper function to normalize metrics to 0-1 scale
export function normalizeMetric(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

// Calculate confidence interval for rank prediction
export function calculateRankConfidence(baseRank: number): { lower: number; upper: number } {
  const variance = 5; // +/- 5 positions confidence interval
  return {
    lower: Math.max(1, Math.round(baseRank - variance)),
    upper: Math.min(200, Math.round(baseRank + variance))
  };
}

export const DEFAULT_BASELINE_METRICS: CategoryMetrics = {
  outcomes: 0.65,            // Adjusted baseline for higher weight
  expertOpinion: 0.60,       // Peer assessment starting point
  facultyResources: 0.70,    // Faculty metrics baseline
  financialResources: 0.65,  // Financial resources baseline
  studentExcellence: 0.70,   // Student excellence baseline
  facultyResearch: 0.60,     // Research baseline
}; 
